/**
 * SectionView — clipping-plane cutaway with colour-coded layer caps
 * (issue 079).
 *
 * Mechanism (composes with layer ownership, never seizes geometry):
 *  1. `gl.localClippingEnabled = true` and a single world-space
 *     `THREE.Plane` shared by every clipped material + every cap mesh.
 *     Position/axis changes mutate that one plane in place, so they cost
 *     nothing beyond moving 7 cap quads.
 *  2. Each sauna-body mesh (classified by material identity) gets its
 *     shared material swapped for a per-section CLONE carrying the clip
 *     plane — the shared singleton is never mutated, so other views are
 *     unaffected (snapshot/PDF/etc). Originals are stashed and restored.
 *  3. Caps: the classic stencil shadow-volume recipe, one cap quad per
 *     material family, colour-coded and resettable. Gated to `balanced`+
 *     via `capsEnabled`; on `low` the double-sided clones already make the
 *     cut read as filled.
 *
 * Environment (ground / grass / dressing / sky) is excluded automatically
 * because those materials are not in the section material→family map.
 */
import { Html } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import {
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import * as THREE from "three";
import type { Market } from "../../domain/location/types";
import {
	applyVentilationDiagramMode,
	cloneMaterialForSection,
	createSectionCapMaterial,
	createSectionStencilMaterials,
} from "../../engines/materials";
import { getT } from "../../i18n";
import type { OrbitControlsRef } from "./SceneControls";
import {
	classifyMaterialFamily,
	getSectionLayerDefinition,
	isSectionClippableMaterial,
	resolveSectionPlaneParams,
	type SectionAxis,
	type SectionLayerFamily,
} from "./sectionLayers";

interface SectionViewProps {
	active: boolean;
	axis: SectionAxis;
	/** Normalized 0..1 position of the cut along `axis`. */
	position: number;
	flip?: boolean;
	/** Stencil caps gated to balanced+ (runtimeProfile.sectionCapsEnabled). */
	capsEnabled: boolean;
	interactive?: boolean;
	/**
	 * Ventilation analysis layer state. The cut replaces each body material
	 * with a per-section clone, so a diagram swap made while the section is
	 * active would land on a clone and be lost on restore — this prop lets the
	 * section re-clone from the freshly swapped materials instead.
	 */
	ventilationDiagram?: boolean;
	market?: Market;
	/**
	 * Bumped once per settled whole-building rotation.
	 *
	 * `buildCaps` freezes each clipped mesh's `matrixWorld` into a stencil
	 * shell (`matrixAutoUpdate = false`), so turning the sauna rig underneath
	 * leaves the coloured caps floating beside the building. The position /
	 * axis / flip effect below does NOT cover this — it only moves the shared
	 * plane — and the `useFrame` catch-up only fires for NEWLY clipped meshes,
	 * of which a rotation produces none. The signal comes from `SaunaRig`
	 * rather than the raw angle because only it can tell when the transform
	 * has actually reached the group.
	 *
	 * The UI gates rotation while the section is open, but the store can still
	 * be changed from outside the viewer — undo/redo, loading a design — so
	 * the gate cannot be the only defence.
	 */
	rigRevision?: number;
	controlsRef?: RefObject<OrbitControlsRef | null>;
	/** Driven by the scene drag handle. */
	onPositionChange?: (position: number) => void;
	/** Families present in the current cut — drives the HUD legend. */
	onFamiliesChange?: (families: SectionLayerFamily[]) => void;
}

const CLIPPED_FLAG = "__sectionClipped";
const ORIG_MATERIAL = "__sectionOrigMaterial";
const CAP_FLAG = "__sectionCap";
const CAP_RENDER_ORDER_BASE = 2000;

type SectionMesh = THREE.Mesh & {
	[CLIPPED_FLAG]?: boolean;
	[ORIG_MATERIAL]?: THREE.Material | THREE.Material[];
	[CAP_FLAG]?: boolean;
};

export function SectionView({
	active,
	axis,
	position,
	flip = false,
	capsEnabled,
	interactive = true,
	ventilationDiagram = false,
	market,
	rigRevision = 0,
	controlsRef,
	onPositionChange,
	onFamiliesChange,
}: SectionViewProps) {
	const scene = useThree((s) => s.scene);
	const camera = useThree((s) => s.camera);
	const gl = useThree((s) => s.gl);
	const invalidate = useThree((s) => s.invalidate);

	const planeRef = useRef<THREE.Plane>(
		new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0),
	);
	const clonesRef = useRef<Set<THREE.Material>>(new Set());
	const capGroupRef = useRef<THREE.Group | null>(null);
	const boundsRef = useRef<THREE.Box3>(new THREE.Box3());
	const wasActiveRef = useRef(false);
	// Keep the latest control inputs available to imperative helpers
	// without re-running the heavy activate effect on every drag tick.
	const axisRef = useRef(axis);
	axisRef.current = axis;
	const positionRef = useRef(position);
	positionRef.current = position;
	const flipRef = useRef(flip);
	flipRef.current = flip;
	const capsEnabledRef = useRef(capsEnabled);
	capsEnabledRef.current = capsEnabled;
	const onFamiliesChangeRef = useRef(onFamiliesChange);
	onFamiliesChangeRef.current = onFamiliesChange;
	const familiesSigRef = useRef<string | null>(null);
	const dragControllerRef = useRef<{ dispose: () => void } | null>(null);
	// Re-render once after activation populates bounds (the activate effect
	// runs post-render, and invalidate() drives the R3F loop, not React) so
	// the scene drag handle appears without waiting for a prop change.
	const [ready, setReady] = useState(false);

	// ── Plane / cap geometry math ─────────────────────────────

	const updatePlane = useCallback(() => {
		const bounds = boundsRef.current;
		if (bounds.isEmpty()) return;
		const params = resolveSectionPlaneParams(
			axisRef.current,
			positionRef.current,
			{ min: bounds.min, max: bounds.max },
			flipRef.current,
		);
		planeRef.current.normal.set(
			params.normal[0],
			params.normal[1],
			params.normal[2],
		);
		planeRef.current.constant = params.constant;
	}, []);

	const positionCapPlanes = useCallback(() => {
		const group = capGroupRef.current;
		const bounds = boundsRef.current;
		if (!group || bounds.isEmpty()) return;
		const center = bounds.getCenter(new THREE.Vector3());
		const params = resolveSectionPlaneParams(
			axisRef.current,
			positionRef.current,
			{ min: bounds.min, max: bounds.max },
			flipRef.current,
		);
		for (const child of group.children) {
			if (child.userData.capPlane !== true) continue;
			if (axisRef.current === "x") {
				child.position.set(params.coord, center.y, center.z);
				child.rotation.set(0, Math.PI / 2, 0);
			} else {
				child.position.set(center.x, center.y, params.coord);
				child.rotation.set(0, 0, 0);
			}
		}
	}, []);

	// ── Clip application / restoration ────────────────────────

	const applyClips = useCallback((): boolean => {
		const plane = planeRef.current;
		let clippedAny = false;
		const families = new Set<SectionLayerFamily>();
		const bounds = boundsRef.current;
		bounds.makeEmpty();
		scene.traverse((obj) => {
			if (!(obj instanceof THREE.Mesh)) return;
			const mesh = obj as SectionMesh;
			if (mesh[CAP_FLAG]) return;
			const current = mesh.material;
			if (!mesh[CLIPPED_FLAG]) {
				if (!isSectionClippableMaterial(current)) return;
				mesh[ORIG_MATERIAL] = current;
				const planes = [plane];
				const clone = Array.isArray(current)
					? current.map((m) => {
							const c = cloneMaterialForSection(m, planes);
							clonesRef.current.add(c);
							return c;
						})
					: (() => {
							const c = cloneMaterialForSection(current, planes);
							clonesRef.current.add(c);
							return c;
						})();
				mesh.material = clone;
				mesh[CLIPPED_FLAG] = true;
				clippedAny = true;
			}
			// Bounds + legend reflect only currently-visible body layers.
			const family = classifyMaterialFamily(mesh[ORIG_MATERIAL]);
			if (family && mesh.visible) {
				families.add(family);
				// expandByObject refreshes the mesh's world matrix itself.
				bounds.expandByObject(mesh);
			}
		});
		if (!bounds.isEmpty()) {
			updatePlane();
		}
		// Emit the legend families only when the SET changes — applyClips
		// runs every rendered frame (camera orbit included), so emitting an
		// unconditional new array would churn React state on every frame.
		const signature = Array.from(families).sort().join("|");
		if (signature !== familiesSigRef.current) {
			familiesSigRef.current = signature;
			onFamiliesChangeRef.current?.(Array.from(families));
		}
		return clippedAny;
	}, [scene, updatePlane]);

	const disposeCaps = useCallback(() => {
		const group = capGroupRef.current;
		if (!group) return;
		group.traverse((obj) => {
			if (obj instanceof THREE.Mesh) {
				// Stencil/cap meshes either share body geometry (don't
				// dispose) or own a unit plane geometry (do). We flag the
				// owned geometry so shared body geometry is never freed.
				if (obj.userData.ownGeometry === true) {
					obj.geometry.dispose();
				}
				const mat = obj.material;
				if (Array.isArray(mat)) {
					for (const m of mat) m.dispose();
				} else {
					mat.dispose();
				}
			}
		});
		scene.remove(group);
		capGroupRef.current = null;
	}, [scene]);

	const buildCaps = useCallback(() => {
		disposeCaps();
		if (!capsEnabledRef.current) return;
		const bounds = boundsRef.current;
		if (bounds.isEmpty()) return;

		// Group clipped meshes by family.
		const meshesByFamily = new Map<SectionLayerFamily, SectionMesh[]>();
		scene.traverse((obj) => {
			if (!(obj instanceof THREE.Mesh)) return;
			const mesh = obj as SectionMesh;
			if (mesh[CAP_FLAG] || !mesh[CLIPPED_FLAG] || !mesh.visible) return;
			const family = classifyMaterialFamily(mesh[ORIG_MATERIAL]);
			if (!family) return;
			const list = meshesByFamily.get(family);
			if (list) list.push(mesh);
			else meshesByFamily.set(family, [mesh]);
		});
		if (meshesByFamily.size === 0) return;

		const group = new THREE.Group();
		group.name = "detail/section/caps";
		group.userData[CAP_FLAG] = true;

		const diag = bounds.getSize(new THREE.Vector3()).length() * 1.2 || 6;
		const planes = [planeRef.current];
		let familyIndex = 0;
		for (const [family, meshes] of meshesByFamily) {
			const baseOrder = CAP_RENDER_ORDER_BASE + familyIndex * 2;
			const stencil = createSectionStencilMaterials(planes);
			for (const mesh of meshes) {
				mesh.updateWorldMatrix(true, false);
				for (const stencilMat of [stencil.back, stencil.front]) {
					const sm = new THREE.Mesh(mesh.geometry, stencilMat) as SectionMesh;
					sm[CAP_FLAG] = true;
					sm.matrixAutoUpdate = false;
					sm.matrix.copy(mesh.matrixWorld);
					sm.renderOrder = baseOrder;
					group.add(sm);
				}
			}
			// One colour cap quad per family, masked by the stencil and
			// resetting the stencil after it draws so families don't bleed.
			const def = getSectionLayerDefinition(family);
			const capMat = createSectionCapMaterial(def.capColor);
			const capPlane = new THREE.Mesh(
				new THREE.PlaneGeometry(diag, diag),
				capMat,
			) as SectionMesh;
			capPlane[CAP_FLAG] = true;
			capPlane.userData.capPlane = true;
			capPlane.userData.ownGeometry = true;
			capPlane.renderOrder = baseOrder + 1;
			capPlane.onAfterRender = (renderer) => {
				(renderer as THREE.WebGLRenderer).clearStencil?.();
			};
			group.add(capPlane);
			familyIndex += 1;
		}

		scene.add(group);
		capGroupRef.current = group;
		positionCapPlanes();
	}, [scene, disposeCaps, positionCapPlanes]);

	const restoreClips = useCallback(() => {
		scene.traverse((obj) => {
			if (!(obj instanceof THREE.Mesh)) return;
			const mesh = obj as SectionMesh;
			if (!mesh[CLIPPED_FLAG]) return;
			const orig = mesh[ORIG_MATERIAL];
			if (orig) mesh.material = orig;
			delete mesh[CLIPPED_FLAG];
			delete mesh[ORIG_MATERIAL];
		});
		for (const clone of clonesRef.current) clone.dispose();
		clonesRef.current.clear();
	}, [scene]);

	// ── Activate / deactivate ─────────────────────────────────

	useEffect(() => {
		if (active && !wasActiveRef.current) {
			gl.localClippingEnabled = true;
			applyClips();
			buildCaps();
			wasActiveRef.current = true;
			setReady(true);
			invalidate();
		} else if (!active && wasActiveRef.current) {
			disposeCaps();
			restoreClips();
			familiesSigRef.current = null;
			onFamiliesChangeRef.current?.([]);
			gl.localClippingEnabled = false;
			wasActiveRef.current = false;
			setReady(false);
			invalidate();
		}
	}, [
		active,
		gl,
		applyClips,
		buildCaps,
		disposeCaps,
		restoreClips,
		invalidate,
	]);

	// Tear down on unmount regardless of `active`.
	useEffect(
		() => () => {
			if (!wasActiveRef.current) return;
			disposeCaps();
			restoreClips();
			gl.localClippingEnabled = false;
			wasActiveRef.current = false;
		},
		[gl, disposeCaps, restoreClips],
	);

	// Position / axis / flip changes: just move the shared plane + the
	// cap quads. No re-clone, no stencil rebuild. The ref syncs here keep
	// the trigger values "used" (the imperative helpers read the refs).
	useEffect(() => {
		if (!wasActiveRef.current) return;
		axisRef.current = axis;
		positionRef.current = position;
		flipRef.current = flip;
		updatePlane();
		positionCapPlanes();
		invalidate();
	}, [axis, position, flip, updatePlane, positionCapPlanes, invalidate]);

	// Ventilation diagram toggled while the section is active: the clipped
	// clones hide the swap, so restore the real materials, let the swap land
	// on them, and re-clone. Same shape as the caps effect below.
	useEffect(() => {
		if (!wasActiveRef.current) return;
		restoreClips();
		applyVentilationDiagramMode(scene, ventilationDiagram);
		applyClips();
		buildCaps();
		invalidate();
	}, [
		ventilationDiagram,
		scene,
		restoreClips,
		applyClips,
		buildCaps,
		invalidate,
	]);

	// Caps toggled (tier change) while active: build or tear down caps.
	useEffect(() => {
		if (!wasActiveRef.current) return;
		capsEnabledRef.current = capsEnabled;
		buildCaps();
		invalidate();
	}, [capsEnabled, buildCaps, invalidate]);

	// The building was turned under an open cut — see `rigRevision`. Re-run
	// applyClips first so the bounds and the shared plane describe the new
	// world extents, then rebuild the shells from the fresh matrices.
	const lastRigRevisionRef = useRef(rigRevision);
	useEffect(() => {
		if (lastRigRevisionRef.current === rigRevision) return;
		lastRigRevisionRef.current = rigRevision;
		if (!wasActiveRef.current) return;
		applyClips();
		buildCaps();
		invalidate();
	}, [rigRevision, applyClips, buildCaps, invalidate]);

	// Catch meshes added by a body rebuild (dimension change) while the
	// section is active — clip them and rebuild caps. Uses useFrame (NOT a
	// raw rAF) so it stays demand-gated: it only runs on frames the viewer
	// already renders, and never pins the frameloop. Same pattern as
	// BlueprintOverride. New clips re-invalidate once, then it settles.
	useFrame(() => {
		if (!wasActiveRef.current) return;
		const newlyClipped = applyClips();
		if (newlyClipped) {
			buildCaps();
			invalidate();
		}
	});

	// ── Scene drag handle ─────────────────────────────────────

	const startDrag = useCallback(
		(event: ReactPointerEvent<HTMLButtonElement>) => {
			if (!interactive || !onPositionChange || dragControllerRef.current)
				return;
			event.stopPropagation();
			const canvas = gl.domElement;
			const { pointerId } = event;
			try {
				canvas.setPointerCapture(pointerId);
			} catch {
				/* jsdom / unsupported */
			}
			if (controlsRef?.current) controlsRef.current.enabled = false;

			const bounds = boundsRef.current;
			const center = bounds.getCenter(new THREE.Vector3());
			const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -center.y);
			const raycaster = new THREE.Raycaster();
			const ndc = new THREE.Vector2();
			const hit = new THREE.Vector3();

			const toPosition = (clientX: number, clientY: number) => {
				const rect = canvas.getBoundingClientRect();
				ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
				ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
				raycaster.setFromCamera(ndc, camera);
				if (!raycaster.ray.intersectPlane(dragPlane, hit)) return null;
				const ax = axisRef.current;
				const min = bounds.min[ax];
				const max = bounds.max[ax];
				if (max <= min) return null;
				return Math.min(1, Math.max(0, (hit[ax] - min) / (max - min)));
			};

			const handleMove = (e: PointerEvent) => {
				const next = toPosition(e.clientX, e.clientY);
				if (next !== null) onPositionChange(next);
			};
			const finish = () => {
				canvas.removeEventListener("pointermove", handleMove);
				canvas.removeEventListener("pointerup", finish);
				canvas.removeEventListener("pointercancel", finish);
				try {
					canvas.releasePointerCapture(pointerId);
				} catch {
					/* already released */
				}
				if (controlsRef?.current) controlsRef.current.enabled = true;
				dragControllerRef.current = null;
				invalidate();
			};
			canvas.addEventListener("pointermove", handleMove);
			canvas.addEventListener("pointerup", finish);
			canvas.addEventListener("pointercancel", finish);
			dragControllerRef.current = { dispose: finish };
		},
		[interactive, onPositionChange, gl, controlsRef, camera, invalidate],
	);

	// Release an in-flight drag if the handle unmounts mid-gesture.
	useEffect(
		() => () => {
			dragControllerRef.current?.dispose();
			dragControllerRef.current = null;
		},
		[],
	);

	if (!active || !ready) return null;

	const bounds = boundsRef.current;
	if (bounds.isEmpty()) return null;
	const center = bounds.getCenter(new THREE.Vector3());
	const params = resolveSectionPlaneParams(
		axis,
		position,
		{ min: bounds.min, max: bounds.max },
		flip,
	);
	const handlePosition: [number, number, number] =
		axis === "x"
			? [params.coord, bounds.max.y + 0.12, center.z]
			: [center.x, bounds.max.y + 0.12, params.coord];
	const t = getT(market ?? "se");

	return (
		<Html
			position={handlePosition}
			center
			transform={false}
			pointerEvents={interactive ? "auto" : "none"}
			zIndexRange={[40, 0]}
		>
			{interactive ? (
				<button
					type="button"
					className="viewer-drag-affordance viewer-drag-affordance--section"
					aria-label={t.viewer_sectionPositionLabel}
					onPointerDown={startDrag}
				>
					<span className="viewer-drag-affordance__label">
						{t.viewer_sectionPositionLabel}
					</span>
					<svg
						aria-hidden="true"
						focusable="false"
						width="40"
						height="12"
						viewBox="0 0 40 12"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M8 6 L2 6 M2 6 L6 2 M2 6 L6 10"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M32 6 L38 6 M38 6 L34 2 M38 6 L34 10"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</button>
			) : (
				<div
					className="viewer-drag-affordance viewer-drag-affordance--section"
					aria-hidden="true"
				/>
			)}
		</Html>
	);
}
